import { Link, useRouteMatch } from "react-router-dom";

import styled from "styled-components/macro";

import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { SlotsTakenCount } from "Signups/SignupView/Shared/SlotsTakenCount";
import { useSignups } from "Signups/state.js";
import { useIsSignupEditMode } from "Signups/stateHooks";
import classNames from "classnames";
import { motion } from "framer-motion";

import FnrIcon from "UI/FnrIcon";
import { HoverChild } from "UI/HoverChildren";

import { formatNumberWithCommas } from "Utilities/Number";

export default function SignupItem({
  item: { ID: itemID, name, numberOfRespondents },
  onPointerDown,
  isActiveItem,
  allItems,
  index,
}) {
  const [
    {
      currentSignupList: { ID: signupListID },
    },
  ] = useSignups();

  const { isSignupEditMode: showReorderIcon } = useIsSignupEditMode();
  const { isArchived } = useIsSignupClosed();

  const itemPath = `/signups/${signupListID}/items/${itemID}`;
  const isSelected = useRouteMatch(itemPath) != null;
  const canReorder = showReorderIcon && allItems.length > 1 && !isArchived;

  const content = () => {
    return (
      <>
        <DragBorder>
          <Background />
          <Inside whileTap={{ scale: 0.95 }}>
            <Title>{name || "New Item"}</Title>
            <SlotsTakenCountContainer>
              {!isSelected && numberOfRespondents && numberOfRespondents > 0 ? (
                <SlotsTakenCountCustom>
                  {formatNumberWithCommas(numberOfRespondents)}
                </SlotsTakenCountCustom>
              ) : null}
            </SlotsTakenCountContainer>
          </Inside>
        </DragBorder>
      </>
    );
  };

  return (
    <>
      <HoverChild selected={isSelected}>
        <Wrapper
          className={classNames({
            selected: isSelected,
            dragging: isActiveItem,
          })}
          onPointerDown={isSelected && onPointerDown ? onPointerDown : null}
          $canReorder={canReorder && isSelected}
        >
          {isSelected ? (
            content()
          ) : (
            <StyledLink data-cy={`SignupItem_${index}`} to={itemPath}>{content()}</StyledLink>
          )}
          {canReorder && (
            <ReorderDrag onPointerDown={onPointerDown}>
              <FnrIcon fas fa-sort />
            </ReorderDrag>
          )}
        </Wrapper>
      </HoverChild>
      {index < allItems.length - 1 && <DividerLine />}
    </>
  );
}

const Wrapper = styled(motion.div)`
  cursor: ${(p) => (p.$canReorder ? "grab" : "pointer")};
  position: relative;
  z-index: 100;

  & a {
    text-decoration: none !important;
    color: inherit;
  }

  &:active {
    cursor: ${(p) => (p.$canReorder ? "grabbing" : "pointer")};
  }
`;

const Background = styled.div`
  border-radius: 8px;
  position: absolute;
  opacity: 0;
  z-index: 1;
  background: #eceff0;
  transition: transform 0.2s, opacity 0.2s, inset 0.2s;
  inset: 5px 6px;

  ${Wrapper}:hover & {
    inset: 2px;
  }

  ${Wrapper}.selected &, ${Wrapper}.dragging & {
    opacity: 1;
    transform: none;
    inset: 0;
  }

  ${Wrapper}.dragging & {
    inset: 2px;
  }
`;

const DragBorder = styled.div`
  position: relative;
  border-radius: 9px;
  box-shadow: rgba(0, 0, 0, 0) 0 0 0;

  transition: box-shadow 0.2s;

  ${Wrapper}.dragging & {
    background: #fff;
    box-shadow: rgba(0, 0, 0, 0.3) 0 0 4px;
  }
`;

const Inside = styled(motion.div)`
  padding: 12px 12px;
  font-weight: 400;
  position: relative;
  z-index: 5;
  text-decoration: none !important;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Title = styled.div`
  user-select: none;
`;

const ReorderDrag = styled(motion.div)`
  position: absolute;
  right: 6px;
  z-index: 100;
  top: 50%;
  margin-top: -12px;
  font-size: 24px;
  width: 24px;
  height: 24px;
  line-height: 24px;
  text-align: center;
  cursor: grab;
  transition: opacity 0.2s, transform 0.2s, visibility 0.2s;
  visibility: hidden;
  opacity: 0;
  transform: scale(0.8);
  touch-action: none;

  &:active {
    cursor: grabbing;
  }

  ${Wrapper}.selected &, ${Wrapper}.dragging & {
    opacity: 0.7;
    visibility: visible;
    transform: none;

    &:hover {
      opacity: 1;
      transform: scale(1.2);
    }
  }
  ${Wrapper}.dragging & {
    visibility: visible;
    opacity: 1;
    transform: scale(1.2);
  }
`;

const SlotsTakenCountCustom = styled(SlotsTakenCount)`
  transition: opacity 0.2s;
`;
const SlotsTakenCountContainer = styled.div`
  flex-shrink: 0;
  min-width: 12px;
`;

const DividerLine = styled.div`
  height: 1px;
  background: red;
  margin: 2px 12px;
  background: #eceff0;
  opacity: 0.8;
`;

const StyledLink = styled(Link)`
  text-decoration: none !important;
  color: inherit;
  cursor: pointer;
`;
